/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Archive,
  ArrowsClockwise,
  Calendar,
  CaretDown,
  Chat,
  Footprints,
  Funnel,
  MagnifyingGlass,
  MapPin,
  Megaphone,
  Palette,
  Plus,
  Star,
  Users,
  X
} from '@phosphor-icons/react';
import GroupCard from './GroupCard';
import { API_BASE as API } from '../apiConfig';


const GROUP_TYPES = [
  { id: 'walk',       label: 'Walking',    icon: Footprints },
  { id: 'discussion', label: 'Discussion', icon: Chat },
  { id: 'event',      label: 'Event',      icon: Calendar },
  { id: 'interest',   label: 'Interest',   icon: Star },
  { id: 'creative',   label: 'Creative',   icon: Palette },
  { id: 'action',     label: 'Action',     icon: Megaphone },
];

export default function GroupsView({ token, user, onOpenGroup, showMessage }) {
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('discover'); // discover | mine | archived | create
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [archivedGroups, setArchivedGroups] = useState([]);

  // Create form state
  const [newGroup, setNewGroup] = useState({
    name: '', description: '', type: 'interest', interests: [],
    maxMembers: '', suburb: '', isPrivate: false,
  });
  const [creating, setCreating] = useState(false);
  const [interestInput, setInterestInput] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // ── Fetch groups ───────────────────────────────────
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (typeFilter) params.set('type', typeFilter);
      const res = await fetch(`${API}/api/connect/groups/discover?${params}`, { headers });
      const data = await res.json();
      if (data.success) setGroups(data.data || []);
    } catch (err) { console.error('Groups fetch error:', err); }
    setLoading(false);
  }, [searchQuery, typeFilter, token]);

  const fetchMyGroups = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/connect/groups/me/joined`, { headers });
      const data = await res.json();
      if (data.success) setMyGroups(data.data || []);
    } catch (err) { console.error('My groups fetch error:', err); }
  }, [token]);

  const fetchArchived = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/connect/groups/archive/browse`, { headers });
      const data = await res.json();
      if (data.success) setArchivedGroups(data.data || []);
    } catch (err) { console.error('Archived fetch error:', err); }
  }, [token]);

  useEffect(() => { fetchGroups(); fetchMyGroups(); }, [fetchGroups, fetchMyGroups]);
  useEffect(() => { if (tab === 'archived') fetchArchived(); }, [tab]);

  // ── Join group ─────────────────────────────────────
  const handleJoin = async (groupId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API}/api/connect/groups/${groupId}/join`, { method: 'POST', headers });
      const data = await res.json();
      if (data.success) {
        showMessage?.('Joined group!');
        fetchGroups(); fetchMyGroups();
      } else {
        showMessage?.(data.message || 'Could not join');
      }
    } catch (err) { showMessage?.('Error joining group'); }
  };

  // ── Create group ───────────────────────────────────
  const handleCreate = async () => {
    if (!newGroup.name.trim()) return showMessage?.('Give your group a name');
    if (!newGroup.description.trim()) return showMessage?.('Add a description');
    setCreating(true);
    try {
      const body = {
        name: newGroup.name.trim(),
        description: newGroup.description.trim(),
        type: newGroup.type,
        interests: newGroup.interests,
        isPrivate: newGroup.isPrivate,
      };
      if (newGroup.maxMembers) body.maxMembers = parseInt(newGroup.maxMembers);
      if (newGroup.suburb) body.location = { suburb: newGroup.suburb };

      const res = await fetch(`${API}/api/connect/groups`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        showMessage?.('Group created!');
        setNewGroup({ name: '', description: '', type: 'interest', interests: [], maxMembers: '', suburb: '', isPrivate: false });
        setTab('mine');
        fetchGroups(); fetchMyGroups();
      } else {
        showMessage?.(data.message || 'Could not create group');
      }
    } catch (err) { showMessage?.('Error creating group'); }
    setCreating(false);
  };

  const addInterest = () => {
    const tag = interestInput.trim();
    if (tag && !newGroup.interests.includes(tag)) {
      setNewGroup(p => ({ ...p, interests: [...p.interests, tag] }));
    }
    setInterestInput('');
  };

  const myGroupIds = new Set(myGroups.map(g => g._id));

  // ── Styles ─────────────────────────────────────────
  const S = {
    card: { background: '#18181b', border: '1px solid #27272a', borderRadius: 12, padding: '14px 16px', marginBottom: 10 },
    input: {
      width: '100%', padding: '10px 14px', background: '#18181b', border: '1px solid #27272a',
      borderRadius: 10, color: '#fafafa', fontSize: 14, fontFamily: 'inherit', outline: 'none',
      boxSizing: 'border-box',
    },
    label: { color: '#a1a1aa', fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' },
    teal: '#2dd4bf',
    btn: (active) => ({
      padding: '7px 14px', border: 'none', borderRadius: 10, cursor: 'pointer',
      fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.2s',
      background: active ? '#18181b' : 'transparent',
      color: active ? '#2dd4bf' : 'rgba(255,255,255,0.35)',
    }),
  };

  return (
    <div style={{ padding: '0 20px 20px' }}>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3, marginBottom: 14 }}>
        {[
          { id: 'discover', label: 'Discover' },
          { id: 'mine', label: 'My Groups' },
          { id: 'archived', label: 'Archived' },
          { id: 'create', label: '+ Create' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={S.btn(tab === t.id)} aria-label={t.label}
            aria-current={tab === t.id ? 'page' : undefined}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ DISCOVER ═══ */}
      {tab === 'discover' && (
        <>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <MagnifyingGlass size={16} color="#71717a" style={{ position: 'absolute', left: 12, top: 11 }} aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search groups..."
              aria-label="Search groups"
              style={{ ...S.input, paddingLeft: 36 }}
            />
          </div>

          {/* Type filter chips */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <button onClick={() => setTypeFilter('')}
              style={{ ...S.btn(!typeFilter), border: !typeFilter ? '1px solid #2dd4bf' : '1px solid transparent', borderRadius: 20, padding: '5px 12px' }}>
              All
            </button>
            {GROUP_TYPES.map(gt => {
              const Icon = gt.icon;
              const active = typeFilter === gt.id;
              return (
                <button key={gt.id} onClick={() => setTypeFilter(active ? '' : gt.id)}
                  aria-label={`Filter by ${gt.label}`}
                  aria-pressed={active}
                  style={{
                    ...S.btn(active), border: active ? '1px solid #2dd4bf' : '1px solid transparent',
                    borderRadius: 20, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                  <Icon size={12} aria-hidden="true" /> {gt.label}
                </button>
              );
            })}
          </div>

          {/* Groups list */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#71717a' }}>
              <ArrowsClockwise size={20} className="spin" aria-hidden="true" />
              <p style={{ marginTop: 8, fontSize: 13 }}>Loading groups...</p>
            </div>
          ) : groups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Users size={32} color="#3f3f46" aria-hidden="true" />
              <p style={{ color: '#71717a', fontSize: 13, marginTop: 8 }}>No groups found</p>
              <button onClick={() => setTab('create')} style={{
                marginTop: 12, padding: '8px 20px', background: '#2dd4bf', color: '#000',
                border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              }}>Create one</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="list" aria-label="Available groups">
              {groups.map(g => (
                <div key={g._id} role="listitem">
                  <GroupCard
                    group={g}
                    isMember={myGroupIds.has(g._id)}
                    onClick={() => myGroupIds.has(g._id) ? onOpenGroup?.(g) : handleJoin(g._id)}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ MY GROUPS ═══ */}
      {tab === 'mine' && (
        <>
          {myGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Users size={32} color="#3f3f46" aria-hidden="true" />
              <p style={{ color: '#71717a', fontSize: 13, marginTop: 8 }}>You haven't joined any groups yet</p>
              <button onClick={() => setTab('discover')} style={{
                marginTop: 12, padding: '8px 20px', background: '#2dd4bf', color: '#000',
                border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              }}>Discover groups</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="list" aria-label="Your groups">
              {myGroups.map(g => (
                <div key={g._id} role="listitem">
                  <GroupCard group={g} isMember onClick={() => onOpenGroup?.(g)} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ ARCHIVED ═══ */}
      {tab === 'archived' && (
        <>
          {archivedGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Archive size={32} color="#3f3f46" aria-hidden="true" />
              <p style={{ color: '#71717a', fontSize: 13, marginTop: 8 }}>No archived groups</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="list" aria-label="Archived groups">
              {archivedGroups.map(g => (
                <div key={g._id} role="listitem">
                  <GroupCard group={g} isArchived onClick={() => onOpenGroup?.(g)} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ CREATE ═══ */}
      {tab === 'create' && (
        <div style={S.card}>
          <h3 style={{ color: '#fafafa', fontSize: 16, fontWeight: 700, margin: '0 0 16px' }}>Create a Group</h3>

          {/* Name */}
          <label style={S.label} htmlFor="group-name">Group name</label>
          <input id="group-name" style={{ ...S.input, marginBottom: 12 }}
            value={newGroup.name} onChange={e => setNewGroup(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Townsville Morning Walkers" maxLength={100} />

          {/* Description */}
          <label style={S.label} htmlFor="group-desc">Description</label>
          <textarea id="group-desc" rows={3}
            style={{ ...S.input, marginBottom: 12, resize: 'vertical' }}
            value={newGroup.description} onChange={e => setNewGroup(p => ({ ...p, description: e.target.value }))}
            placeholder="What is this group about?" maxLength={500} />

          {/* Type */}
          <label style={S.label}>Group type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {GROUP_TYPES.map(gt => {
              const Icon = gt.icon;
              const active = newGroup.type === gt.id;
              return (
                <button key={gt.id}
                  onClick={() => setNewGroup(p => ({ ...p, type: gt.id }))}
                  aria-label={gt.label}
                  aria-pressed={active}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
                    border: active ? '1px solid #2dd4bf' : '1px solid #27272a',
                    background: active ? 'rgba(45,212,191,0.1)' : 'transparent',
                    color: active ? '#2dd4bf' : '#a1a1aa', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  }}>
                  <Icon size={13} aria-hidden="true" /> {gt.label}
                </button>
              );
            })}
          </div>

          {/* Interests */}
          <label style={S.label} htmlFor="group-interests">Interests / tags</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input id="group-interests" style={{ ...S.input, flex: 1 }}
              value={interestInput} onChange={e => setInterestInput(e.target.value)}
              placeholder="Add an interest tag"
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addInterest())} />
            <button onClick={addInterest} style={{
              padding: '8px 14px', background: '#27272a', border: 'none', borderRadius: 10,
              color: '#fafafa', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
            }} aria-label="Add interest tag">
              <Plus size={16} />
            </button>
          </div>
          {newGroup.interests.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {newGroup.interests.map(tag => (
                <span key={tag} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'rgba(45,212,191,0.1)', color: '#2dd4bf',
                  fontSize: 11, padding: '4px 10px', borderRadius: 12, fontWeight: 600,
                }}>
                  {tag}
                  <X size={10} style={{ cursor: 'pointer' }} aria-label={`Remove ${tag}`}
                    onClick={() => setNewGroup(p => ({ ...p, interests: p.interests.filter(t => t !== tag) }))} />
                </span>
              ))}
            </div>
          )}

          {/* Location */}
          <label style={S.label} htmlFor="group-location">Location (suburb)</label>
          <input id="group-location" style={{ ...S.input, marginBottom: 12 }}
            value={newGroup.suburb} onChange={e => setNewGroup(p => ({ ...p, suburb: e.target.value }))}
            placeholder="e.g. Townsville" />

          {/* Max members */}
          <label style={S.label} htmlFor="group-max">Max members (optional)</label>
          <input id="group-max" type="number" style={{ ...S.input, marginBottom: 12 }}
            value={newGroup.maxMembers} onChange={e => setNewGroup(p => ({ ...p, maxMembers: e.target.value }))}
            placeholder="No limit" min={2} />

          {/* Private toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
            <div onClick={() => setNewGroup(p => ({ ...p, isPrivate: !p.isPrivate }))}
              role="switch" aria-checked={newGroup.isPrivate} tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setNewGroup(p => ({ ...p, isPrivate: !p.isPrivate }))}
              style={{
                width: 40, height: 22, borderRadius: 11, padding: 2,
                background: newGroup.isPrivate ? '#2dd4bf' : '#3f3f46', transition: 'all 0.2s', cursor: 'pointer',
              }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transform: newGroup.isPrivate ? 'translateX(18px)' : 'translateX(0)', transition: 'all 0.2s',
              }} />
            </div>
            <span style={{ color: '#d4d4d8', fontSize: 13 }}>Private group (invite only)</span>
          </label>

          {/* Submit */}
          <button onClick={handleCreate} disabled={creating}
            aria-label="Create group"
            style={{
              width: '100%', padding: '12px', background: creating ? '#27272a' : '#2dd4bf',
              color: creating ? '#71717a' : '#000', border: 'none', borderRadius: 12,
              fontWeight: 700, fontSize: 14, cursor: creating ? 'default' : 'pointer', fontFamily: 'inherit',
            }}>
            {creating ? 'Creating...' : 'Create Group'}
          </button>
        </div>
      )}
    </div>
  );
}
